import { flip, offset, shift } from '@floating-ui/react';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useFloatingElement } from '@/hooks/use-floating-element';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { getTextSelectionBoundingRect } from '@/lib/tiptap-advanced-utils';
import { isSelectionInsideZydefn } from '@/lib/tiptap-utils';

import { mathEditingPluginKey } from '@/components/tiptap-editor/tiptap-extension/math-extension/math-editing-extension';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';

const FLOATING_Z_INDEX = 1000;
const HOVER_CLOSE_DELAY_MS = 200;

export interface ZydefTermDblClickPopoverProps {
    editor?: Editor | null;
}

type HoverInlineMathContext = {
    pos: number;
    anchorRect: DOMRect;
    inZydefTerm: boolean;
};

function isMathBeingEdited(editor: Editor) {
    return mathEditingPluginKey.getState(editor.state)?.pendingEdit != null;
}

function canShowZydefTermPopover(editor: Editor) {
    if (isMathBeingEdited(editor)) {
        return false;
    }

    const { selection } = editor.state;
    if (!selection.empty) {
        return isSelectionInsideZydefn(editor) && editor.can().setZydefTerm();
    }
    return editor.isActive('zydefTerm');
}

function getPopoverAnchorRect(editor: Editor): DOMRect | null {
    const selectionRect = getTextSelectionBoundingRect(editor);
    if (selectionRect) {
        return selectionRect;
    }

    const { selection } = editor.state;
    if (!selection.empty || !editor.isActive('zydefTerm')) {
        return null;
    }

    try {
        const coords = editor.view.coordsAtPos(selection.from);
        return new DOMRect(coords.left, coords.top, Math.max(coords.right - coords.left, 1), Math.max(coords.bottom - coords.top, 1));
    } catch {
        return null;
    }
}

/**
 * CommentsKit inline-thread decorations set `data-type="inline"` on the math DOM, overwriting our
 * `data-type="inline-math"`. Resolve identity via the ProseMirror node, not that attribute.
 */
function getInlineMathHoverContext(editor: Editor, target: EventTarget | null): HoverInlineMathContext | null {
    if (!(target instanceof Element) || !editor.view.dom.contains(target)) return null;

    const mathEl = target.closest('.tiptap-mathematics-render');
    if (!(mathEl instanceof HTMLElement) || !editor.view.dom.contains(mathEl)) return null;

    const inlineMathPos = editor.view.posAtDOM(mathEl, 0);
    if (inlineMathPos == null) return null;

    const node = editor.state.doc.nodeAt(inlineMathPos);
    if (!node || node.type.name !== 'inlineMath') return null;

    const $pos = editor.state.doc.resolve(inlineMathPos);
    let inZydefn = false;
    let inZydefTerm = false;
    for (let depth = $pos.depth; depth > 0; depth--) {
        const name = $pos.node(depth)?.type?.name;
        if (name === 'zydefn' || name === 'zydefnBlock') inZydefn = true;
        if (name === 'zydefTerm') inZydefTerm = true;
    }
    if (!inZydefn) return null;

    return {
        pos: inlineMathPos,
        anchorRect: mathEl.getBoundingClientRect(),
        inZydefTerm,
    };
}

function getInlineMathAnchorRect(editor: Editor, pos: number): DOMRect | null {
    const dom = editor.view.nodeDOM(pos);
    if (dom instanceof Element) {
        return dom.getBoundingClientRect();
    }
    return null;
}

export function ZydefTermDblClickPopover({ editor: providedEditor }: ZydefTermDblClickPopoverProps) {
    const { editor } = useTiptapEditor(providedEditor);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const [hoverInlineMath, setHoverInlineMath] = useState<HoverInlineMathContext | null>(null);
    const hoverInlineMathRef = useRef<HoverInlineMathContext | null>(null);
    const [mathBeingEdited, setMathBeingEdited] = useState(false);
    const [dismissedByOutsideClick, setDismissedByOutsideClick] = useState(false);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    const popoverOpenRef = useRef(false);
    const lastSelectionKeyRef = useRef<string | null>(null);
    const isPointerOverPopoverRef = useRef(false);
    const hoverCloseTimerRef = useRef<number | null>(null);

    const setAnchor = useCallback((rect: DOMRect | null) => {
        popoverOpenRef.current = rect != null;
        setAnchorRect(rect);
    }, []);

    useEffect(() => {
        hoverInlineMathRef.current = hoverInlineMath;
    }, [hoverInlineMath]);

    const clearHoverCloseTimer = useCallback(() => {
        if (hoverCloseTimerRef.current != null) {
            window.clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
        }
    }, []);

    const scheduleHoverClose = useCallback(() => {
        clearHoverCloseTimer();
        hoverCloseTimerRef.current = window.setTimeout(() => {
            if (!isPointerOverPopoverRef.current) {
                setHoverInlineMath(null);
            }
            hoverCloseTimerRef.current = null;
        }, HOVER_CLOSE_DELAY_MS);
    }, [clearHoverCloseTimer]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            const id = requestAnimationFrame(() => {
                setAnchor(null);
                setDismissedByOutsideClick(false);
            });
            return () => {
                cancelAnimationFrame(id);
            };
        }

        const el = editor.view.dom as HTMLElement;

        const selectionKey = () => {
            const { from, to, empty } = editor.state.selection;
            return `${from}:${to}:${empty ? '1' : '0'}`;
        };

        const syncFromSelection = () => {
            const nextSelectionKey = selectionKey();
            if (lastSelectionKeyRef.current !== nextSelectionKey) {
                lastSelectionKeyRef.current = nextSelectionKey;
                setDismissedByOutsideClick(false);
            }

            if (!canShowZydefTermPopover(editor)) {
                setAnchor(null);
                return;
            }

            const next = getPopoverAnchorRect(editor);
            if (!next) {
                setAnchor(null);
                return;
            }
            setAnchor(next);
        };

        const onScroll = () => {
            const currentHoverInlineMath = hoverInlineMathRef.current;
            if (currentHoverInlineMath) {
                const nextRect = getInlineMathAnchorRect(editor, currentHoverInlineMath.pos);
                if (!nextRect) {
                    setHoverInlineMath(null);
                    return;
                }
                setHoverInlineMath({ ...currentHoverInlineMath, anchorRect: nextRect });
                return;
            }
            if (popoverOpenRef.current && canShowZydefTermPopover(editor)) {
                setAnchor(getPopoverAnchorRect(editor));
            }
        };

        const clearPopoverForMathEditing = () => {
            clearHoverCloseTimer();
            setHoverInlineMath(null);
            setAnchor(null);
        };

        const onTransaction = () => {
            const editing = isMathBeingEdited(editor);
            setMathBeingEdited(editing);
            if (editing) {
                clearPopoverForMathEditing();
            }
        };

        const onMouseMove = (e: MouseEvent) => {
            if (isMathBeingEdited(editor)) {
                scheduleHoverClose();
                return;
            }

            const nextHover = getInlineMathHoverContext(editor, e.target);
            if (!nextHover) {
                scheduleHoverClose();
                return;
            }
            clearHoverCloseTimer();
            setHoverInlineMath(prev => {
                if (prev?.pos !== nextHover.pos) {
                    setDismissedByOutsideClick(false);
                }
                return nextHover;
            });
        };

        const onMouseLeave = () => {
            scheduleHoverClose();
        };

        const onPointerDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (el.contains(target) || bubbleRef.current?.contains(target)) {
                setDismissedByOutsideClick(false);
                return;
            }
            if (popoverOpenRef.current) {
                setDismissedByOutsideClick(true);
                clearHoverCloseTimer();
                setHoverInlineMath(null);
                setAnchor(null);
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            clearHoverCloseTimer();
            setHoverInlineMath(null);
            setAnchor(null);
        };

        el.addEventListener('mousemove', onMouseMove);
        el.addEventListener('mouseleave', onMouseLeave);
        editor.on('selectionUpdate', syncFromSelection);
        editor.on('transaction', onTransaction);
        window.addEventListener('scroll', onScroll, { capture: true });
        document.addEventListener('pointerdown', onPointerDown, { capture: true });
        document.addEventListener('keydown', onKeyDown, { capture: true });
        onTransaction(); // eslint-disable-line react-you-might-not-need-an-effect/no-external-store-subscription -- TODO: Rewrite this so we don't need to disable this rule
        syncFromSelection(); // eslint-disable-line react-you-might-not-need-an-effect/no-external-store-subscription -- TODO: Rewrite this so we don't need to disable this rule
        return () => {
            el.removeEventListener('mousemove', onMouseMove);
            el.removeEventListener('mouseleave', onMouseLeave);
            editor.off('selectionUpdate', syncFromSelection);
            editor.off('transaction', onTransaction);
            window.removeEventListener('scroll', onScroll, { capture: true });
            document.removeEventListener('pointerdown', onPointerDown, { capture: true });
            document.removeEventListener('keydown', onKeyDown, { capture: true });
            clearHoverCloseTimer();
        };
    }, [clearHoverCloseTimer, editor, scheduleHoverClose, setAnchor]);

    const activeAnchorRect = hoverInlineMath?.anchorRect ?? anchorRect;
    const show = Boolean(editor && activeAnchorRect) && !dismissedByOutsideClick && !mathBeingEdited;

    const inZydefTerm = hoverInlineMath?.inZydefTerm ?? editor?.isActive('zydefTerm') === true;
    const primaryLabel = inZydefTerm ? 'Remove zydefTerm' : 'Mark as zydefTerm';

    const { ref, style, getFloatingProps, isMounted } = useFloatingElement(show, activeAnchorRect, FLOATING_Z_INDEX, {
        placement: 'bottom-start',
        middleware: [offset(8), flip({ mainAxis: true, crossAxis: false }), shift()],
    });

    const setBubbleRef = useCallback(
        (node: HTMLDivElement | null) => {
            bubbleRef.current = node;
            ref(node);
        },
        [ref],
    );

    const onBubbleMouseEnter = useCallback(() => {
        isPointerOverPopoverRef.current = true;
        clearHoverCloseTimer();
    }, [clearHoverCloseTimer]);

    const onBubbleMouseLeave = useCallback(() => {
        isPointerOverPopoverRef.current = false;
        scheduleHoverClose();
    }, [scheduleHoverClose]);

    const onAction = useCallback(() => {
        if (!editor || editor.isDestroyed) return;
        if (hoverInlineMath) {
            const command = hoverInlineMath.inZydefTerm ? 'unsetZydefTerm' : 'setZydefTerm';
            editor.chain().focus().setNodeSelection(hoverInlineMath.pos)[command]().run();
            setHoverInlineMath(null);
            setAnchor(null);
            return;
        }
        if (!canShowZydefTermPopover(editor)) {
            setAnchor(null);
            return;
        }
        const command = inZydefTerm ? 'unsetZydefTerm' : 'setZydefTerm';
        editor.chain().focus()[command]().run();
        setAnchor(null);
    }, [editor, hoverInlineMath, inZydefTerm, setAnchor]);

    if (!isMounted || !show || !activeAnchorRect || !editor?.isEditable) {
        return null;
    }

    return createPortal(
        <div
            ref={setBubbleRef}
            style={style}
            {...getFloatingProps()}
            className="max-w-[320px]"
            onMouseEnter={onBubbleMouseEnter}
            onMouseLeave={onBubbleMouseLeave}
            role="dialog"
            aria-label={inZydefTerm ? 'Remove zydefTerm' : 'Mark selection as zydefTerm'}
        >
            <Card>
                <CardBody>
                    <Button
                        type="button"
                        onClick={onAction}
                    >
                        {primaryLabel}
                    </Button>
                </CardBody>
            </Card>
        </div>,
        document.body,
    );
}
